import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { SVGFC } from '@repo/toolkit/types'
import {
  AdultPlusChildIcon,
  BOneFileIcon,
  HealthFormIcon,
  IdIcon,
  IdPhotoIcon,
  UserTwoIcon
} from '@repo/ui/icons'

import { EnrollmentProfileMissingDocuments } from '../../api/enrollments/get-enrollment-missing-documents'
import { Paths } from '../../lib/constants/path'
import { AccomodationEnum } from '../../lib/types/enums/accomodation-type/accomodation-type'
import { EnrollmentInformationStatus } from '../../lib/types/enums/enrollment/enrollment-information-status.enum'
import {
  EnrollmentDocumentsType,
  EnrollmentLight
} from '../../lib/types/models/enrollment/enrollment-light'

export const constructCenterPath = (path: string, centerId: number) =>
  Paths.CENTER_DETAILS_PAGE.replace(':id', centerId.toString()).replace('*', '') + path

export const replaceParams = (path: string, params: Record<string, string | number>) => {
  return Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(`:${key}`, value.toString()),
    path
  )
}

export const mapEnrollmentsLightToEnrollmentDocument = (
  enrollment: EnrollmentLight
): EnrollmentDocumentsType => {
  return {
    secureIdentityPicture: enrollment.secureIdentityPicture,
    accomodation: enrollment.enrollmentCenter?.center.accomodation,
    hasTransport: enrollment.mainProposition?.mainTrip?.hasTransport,
    childInfoStatus: enrollment.childInfoStatus,
    childIdentityPapersSubmitted: enrollment.childIdentityPapersSubmitted,
    childHealthPapersSubmitted: enrollment.childHealthPapersSubmitted,
    childPickupSubmitted: enrollment.childPickupSubmitted,
    languageTestSubmission: enrollment.languageTestSubmission
  }
}
export type EnrollmentDocumentStatus = {
  icon: SVGFC
  status: 'completed' | 'missing' | 'unavailable' | 'pending'
  label: string
  color?: TailwindColor
}

export const getEnrollmentDocumentsStatus = (
  documents: EnrollmentDocumentsType
): EnrollmentDocumentStatus[] => {
  const {
    secureIdentityPicture,
    childIdentityPapersSubmitted,
    childHealthPapersSubmitted,
    accomodation,
    hasTransport,
    languageTestSubmission,
    childInfoStatus,
    childPickupSubmitted
  } = documents

  const isCampusResidence = accomodation !== AccomodationEnum.HOST_FAMILY
  const testSubmitted = !!languageTestSubmission?.submittedAt

  const toStatus = (
    completed: boolean,
    unavailable?: boolean
  ): EnrollmentDocumentStatus['status'] =>
    unavailable ? 'unavailable' : completed ? 'completed' : 'missing'

  const getProfileStatus = (): EnrollmentDocumentStatus['status'] => {
    if (childInfoStatus === EnrollmentInformationStatus.COMPLETED) return 'completed'
    if (childInfoStatus === EnrollmentInformationStatus.TO_BE_COMPLETED) return 'pending'
    return 'missing'
  }

  return [
    { icon: IdPhotoIcon, status: toStatus(!!secureIdentityPicture), label: "Photo d'identité" },
    {
      icon: IdIcon,
      status: toStatus(!!childIdentityPapersSubmitted),
      label: "Papiers d'identité"
    },
    {
      icon: HealthFormIcon,
      status: toStatus(!!childHealthPapersSubmitted, !isCampusResidence),
      label: 'Fiche sanitaire de liaison'
    },
    {
      icon: AdultPlusChildIcon,
      status: toStatus(!!childPickupSubmitted, !!hasTransport),
      label: 'Infos retour indiv'
    },
    { icon: BOneFileIcon, status: toStatus(testSubmitted), label: 'Test de niveau' },
    {
      icon: UserTwoIcon,
      status: getProfileStatus(),
      label: 'Profile'
    }
  ]
}

export const getEnrollmentDocumentStatusColorAndVariant = (
  status: EnrollmentDocumentStatus['status']
): { color: TailwindColor; variant: VariantColor } => {
  switch (status) {
    case 'completed':
      return { color: tailwindColors.green.DEFAULT, variant: 'success' }
    case 'pending':
      return { color: tailwindColors.orange.DEFAULT, variant: 'orange' }
    case 'missing':
      return { color: tailwindColors.secondaryRed.DEFAULT, variant: 'danger' }
    case 'unavailable':
      return { color: tailwindColors.grey.DEFAULT, variant: 'grey-light' }
  }
}

export const missingDocumentsMapping = (
  missingDocs: EnrollmentProfileMissingDocuments
): string[] => {
  const missingDocuments: string[] = []
  if (missingDocs.secureIdentityPicture) missingDocuments.push("Photo d'identité")
  if (missingDocs.identityDocuments) missingDocuments.push("Papiers d'identité")
  if (missingDocs.healthDocuments) missingDocuments.push('Fiche sanitaire de liaison')
  if (missingDocs.pickupInformation) missingDocuments.push('Infos retour indiv')
  if (missingDocs.otoTravelInformation) missingDocuments.push('Infos voyage indiv')
  if (missingDocs.languageTest) missingDocuments.push('Test de niveau')
  if (missingDocs.childInfo) missingDocuments.push('Profil')
  return missingDocuments
}
